import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

const ViewPurchaseReturnModal = ({ returnId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!returnId) return;
    setLoading(true);
    apiService
      .getPurchaseReturnShowData(returnId)
      .then((res) => {
        if (res && res.data) {
          setData(res.data);
        } else {
          setData(res);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching purchase return details:', err);
        setError('Failed to load purchase return details.');
        setLoading(false);
      });
  }, [returnId]);

  const handlePrint = () => {
    window.print();
  };

  if (!returnId) return null;

  const returnData = data || {};
  const parentPurchase = returnData.return_parent || null;
  const contact = returnData.contact || {};
  const location = returnData.location || {};
  const refNo = parentPurchase?.ref_no || returnData.ref_no || '';

  // Get lines from purchase_lines or parent_purchase.purchase_lines
  let rawLines = returnData.purchase_lines || [];
  if (rawLines.length === 0 && parentPurchase && parentPurchase.purchase_lines) {
    rawLines = parentPurchase.purchase_lines.filter(
      (l) => parseFloat(l.quantity_returned || 0) > 0 || parseFloat(l.quantity || 0) > 0
    );
  }

  const netTotal = rawLines.reduce((acc, l) => {
    const qty = parseFloat(l.quantity_returned || l.quantity || 0);
    const price = parseFloat(l.purchase_price_inc_tax || l.purchase_price || l.unit_price || 0);
    return acc + qty * price;
  }, 0);

  const taxAmount = parseFloat(returnData.tax_amount || 0);
  const finalTotal = parseFloat(returnData.final_total || netTotal + taxAmount || 0);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          width: '92%',
          maxWidth: '1050px',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '1.25rem 1.75rem',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#ffffff'
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b', fontWeight: '700' }}>
            Purchase Return Details <span style={{ color: '#475569', fontWeight: '600' }}>(Reference No: #{refNo})</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              color: '#64748b',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '0.25rem 0.5rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '1.75rem', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
              <i className="fa fa-spinner fa-spin" style={{ fontSize: '1.75rem', marginBottom: '0.75rem' }}></i>
              <p style={{ margin: 0, fontWeight: '500' }}>Loading purchase return details...</p>
            </div>
          ) : error ? (
            <div style={{ color: '#ef4444', textAlign: 'center', padding: '2rem', fontWeight: '500' }}>{error}</div>
          ) : (
            <>
              {/* Header Overview Columns */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: parentPurchase ? '1fr 1fr' : '1fr',
                  gap: '2rem',
                  marginBottom: '1.75rem',
                  fontSize: '0.9rem',
                  color: '#334155'
                }}
              >
                <div>
                  <h4 style={{ color: '#4338ca', fontSize: '1rem', fontWeight: '700', marginBottom: '0.85rem', marginTop: 0 }}>
                    Purchase Return Details:
                  </h4>
                  <p style={{ margin: '0 0 0.4rem 0' }}>
                    <strong>Return Date:</strong> {returnData.transaction_date ? String(returnData.transaction_date).replace('T', ' ').substring(0, 19) : 'N/A'}
                  </p>
                  <p style={{ margin: '0 0 0.4rem 0' }}>
                    <strong>Supplier:</strong> {contact.supplier_business_name || contact.name || 'N/A'}
                  </p>
                  {contact.mobile && <p style={{ margin: '0 0 0.4rem 0' }}>Mobile: {contact.mobile}</p>}
                  {contact.address_line_1 && <p style={{ margin: '0 0 0.4rem 0' }}>{contact.address_line_1}</p>}
                  {(contact.city || contact.state || contact.country) && (
                    <p style={{ margin: '0 0 0.4rem 0' }}>
                      {[contact.city, contact.state, contact.country, contact.zip_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                  <p style={{ margin: 0 }}>
                    <strong>Business Location:</strong> {location.name || 'N/A'}
                  </p>
                </div>

                {parentPurchase && (
                  <div>
                    <h4 style={{ color: '#4338ca', fontSize: '1rem', fontWeight: '700', marginBottom: '0.85rem', marginTop: 0 }}>
                      Purchase Details:
                    </h4>
                    <p style={{ margin: '0 0 0.4rem 0' }}>
                      <strong>Reference No:</strong> {parentPurchase.ref_no || 'N/A'}
                    </p>
                    <p style={{ margin: 0 }}>
                      <strong>Date:</strong> {parentPurchase.transaction_date ? String(parentPurchase.transaction_date).split('T')[0] : 'N/A'}
                    </p>
                  </div>
                )}
              </div>

              {/* Products Table */}
              <div style={{ overflowX: 'auto', marginBottom: '1.75rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ background: '#10b981', color: '#ffffff', textAlign: 'left' }}>
                      <th style={{ padding: '0.75rem 1rem', width: '50px' }}>#</th>
                      <th style={{ padding: '0.75rem 1rem' }}>Product Name</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Unit Price</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>Return Quantity</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Return Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rawLines.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '1.75rem', color: '#64748b' }}>
                          No products found
                        </td>
                      </tr>
                    ) : (
                      rawLines.map((l, idx) => {
                        const qty = parseFloat(l.quantity_returned || l.quantity || 0);
                        const price = parseFloat(l.purchase_price_inc_tax || l.purchase_price || l.unit_price || 0);
                        const subtotal = qty * price;
                        return (
                          <tr key={l.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '0.75rem 1rem', color: '#64748b' }}>{idx + 1}</td>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#1e293b' }}>
                              {l.product?.name || l.variations?.product_variation?.name || 'Product'}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>${price.toFixed(2)}</td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>{qty.toFixed(2)} Single</td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600' }}>${subtotal.toFixed(2)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals Section */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.75rem' }}>
                <div style={{ width: '340px', fontSize: '0.9rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', color: '#475569' }}>
                    <span><strong>Net Total Amount:</strong></span>
                    <span>${netTotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>
                    <span><strong>Total Return Tax:</strong></span>
                    <span>(+) ${taxAmount.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', color: '#1e293b', fontSize: '1.05rem', fontWeight: '700' }}>
                    <span>Return Total:</span>
                    <span>${finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Activities Section */}
              {returnData.activities && returnData.activities.length > 0 && (
                <div>
                  <h4 style={{ color: '#4338ca', fontSize: '1rem', fontWeight: '700', marginBottom: '0.75rem' }}>Activities:</h4>
                  <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                          <th style={{ padding: '0.6rem 0.85rem' }}>Date</th>
                          <th style={{ padding: '0.6rem 0.85rem' }}>Action</th>
                          <th style={{ padding: '0.6rem 0.85rem' }}>By</th>
                          <th style={{ padding: '0.6rem 0.85rem' }}>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returnData.activities.map((act, idx) => (
                          <tr key={act.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '0.6rem 0.85rem', color: '#64748b' }}>
                              {act.created_at ? String(act.created_at).replace('T', ' ').substring(0, 16) : 'N/A'}
                            </td>
                            <td style={{ padding: '0.6rem 0.85rem', fontWeight: '500' }}>{act.description || 'Added'}</td>
                            <td style={{ padding: '0.6rem 0.85rem' }}>{act.causer?.name || 'Admin'}</td>
                            <td style={{ padding: '0.6rem 0.85rem' }}>
                              Total: <span style={{ background: '#38bdf8', color: '#ffffff', padding: '0.15rem 0.45rem', borderRadius: '4px', fontWeight: '600' }}>${finalTotal.toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '1rem 1.75rem',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '0.75rem',
            background: '#f8fafc',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <button
            type="button"
            onClick={handlePrint}
            style={{
              background: '#4338ca',
              color: '#ffffff',
              border: 'none',
              padding: '0.55rem 1.35rem',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.9rem'
            }}
          >
            <i className="fa fa-print"></i> Print
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#1e293b',
              color: '#ffffff',
              border: 'none',
              padding: '0.55rem 1.35rem',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewPurchaseReturnModal;
